import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import { VFC, KeyboardEventHandler, useRef, useState } from "react";
import { css } from "src/styles/theme";
import { CanvasPosition } from "../types/canvas.types";
import { announce } from "@react-aria/live-announcer";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import cn from "classnames";

type Direction = "up" | "right" | "down" | "left";

export const DRAG_HANDLE_CLASS = "node-drag-handle";

export interface NodeDraggerProps {
  position: CanvasPosition;
  onMove: (position: CanvasPosition) => void;
  scrollIntoView: () => void;
}

export const NodeDragger: VFC<NodeDraggerProps> = (props) => {
  const [isInDragMode, setIsInDragMode] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const onBlurHandler = () => setIsInDragMode(false);
  const onKeyDownHandler: KeyboardEventHandler = (e) => {
    if (e.key === "Enter" && !isInDragMode) {
      announce(messages.start(props.position));
      setIsInDragMode(true);
      return;
    }

    if (e.key === "Escape" || e.key === "Enter") {
      announce(messages.end);
      setIsInDragMode(false);
      return;
    }

    if (!isInDragMode) return;
    e.preventDefault();

    if (e.key === "ArrowUp") return void move("up");
    if (e.key === "ArrowDown") return void move("down");
    if (e.key === "ArrowLeft") return void move("left");
    if (e.key === "ArrowRight") return void move("right");
  };

  const move = (direction: Direction) => {
    const diffX = direction === "left" ? -1 : direction === "right" ? 1 : 0;
    const diffY = direction === "up" ? -1 : direction === "down" ? 1 : 0;
    const newPos: CanvasPosition = {
      nodeId: props.position.nodeId,
      x: props.position.x + diffX,
      y: props.position.y + diffY,
    };

    props.onMove(newPos);
    announce(messages.moved(direction, newPos));
  };

  return (
    <>
      <button
        ref={ref}
        className={cn(DRAG_HANDLE_CLASS, $handle())}
        onKeyDown={onKeyDownHandler}
        onBlur={onBlurHandler}
        /*
          Wait for React 18 to accept aria-description as a valid property.
          It's a warning but will treated as error with CI=true.
          aria-description="Press Enter to start drag mode"
        */
      >
        <span className={$handleContent()}>
          <DragHandleDots2Icon />
          <VisuallyHidden.Root>Move node</VisuallyHidden.Root>
        </span>
      </button>
    </>
  );
};

const messages = {
  start: (pos: CanvasPosition) => `
    Started dragging. Press Enter to exit.
    Press an Arrow Key to move the node on the board.
    Current position: Row ${pos.y + 1}, Column ${pos.x + 1}
  `,
  end: "Drag mode deactivated",
  moved: (dir: Direction, pos: CanvasPosition) => `
    Moved node ${dir}.
    New position: Row ${pos.y + 1}, Column ${pos.x + 1}
  `,
};

const $handle = css({
  applyReset: "btn",
  display: "flex",
  justifyContent: "stretch",
  alignItems: "stretch",
  cursor: "grab",
  margin: "-$2",
  padding: "$2",

  "&:focus": {
    outline: 0,
  },

  "&:active": {
    cursor: "grabbing",
  },
});

const $handleContent = css({
  display: "flex",
  placeItems: "center",
  borderRadius: "$1",
  padding: "0 $1",
  color: "$primaryText",

  [`.${$handle.className}:focus-visible &`]: {
    backgroundColor: "$primaryBg",
  },
});
